import { useEffect, useMemo, useRef, useState } from 'react';
import type { StageProbability } from '../../types';
import { useAnalytics, useFundSettings } from '../../hooks/usePipeline';
import { computeFundBreakdown, trackWidth, type DealBlock, type FundBreakdown, type ReservationMode } from '../../utils/fundBreakdown';
import { ThisWeekPanel } from '../analytics/ThisWeekPanel';
import { PipelineTrendChart } from '../analytics/PipelineTrendChart';
import { PipelineBySector } from '../analytics/PipelineBySector';
import { PipelineByVertical } from '../analytics/PipelineByVertical';
import { CandidateDealsByPerson } from '../analytics/CandidateDealsByPerson';
import { EngagementHeatmap } from '../analytics/EngagementHeatmap';
import styles from './OverviewPage.module.css';

/**
 * The Overview tab, NATIVE since the post-Affinity S2 slice (2026-09-01).
 *
 * **It takes no props and fetches no `/api/pipeline`.** Everything on the page now comes from
 * `useAnalytics()` (`/api/analytics/summary`, rebuilt on `board_v2` + the `dash_*` rollups) and
 * `useFundSettings()`. That is what let `overview` leave `SNAPSHOT_TABS` in `App.tsx`; the frozen
 * snapshot endpoint survives serving Intel alone, per the owner ruling.
 *
 * Two things were REMOVED here rather than repointed, and both were deliberate:
 *
 *  - **The "Origination History" block** (`SectionDivider` + `ActivitySummaryCards` +
 *    `TeamActivityPanel`) was the Metrics tab embedded, and it is the ONLY thing on Overview that
 *    read `/api/analytics/team-activity`. Metrics is retired by the same ruling, and the plan is
 *    explicit that `team_activity_builder` must not be kept alive to serve one card row — so the
 *    block goes with the tab. Its monthly origination counts were frozen-era data in any case: that
 *    builder reads `opportunities` + `stage_transitions`, whose writers died at the cut.
 *  - **`parseMoneyMM`'s input.** The fund bar's deals arrive as `analytics.fund_deals` with a typed
 *    `est_investment_mm`, instead of v1 `Deal` rows carrying a display string.
 */

function fmt(n: number): string {
  return n % 1 === 0 ? `$${n.toFixed(0)}` : `$${n.toFixed(1)}`;
}

// Native-title tooltip for a per-deal block (fund + coinvest segments).
function dealTitle(d: DealBlock): string {
  const base = `${d.name}\nTotal ${fmt(d.total)}mm · Fund ${fmt(d.fund)}mm`;
  return d.coinvest > 0 ? `${base} · Coinvest ${fmt(d.coinvest)}mm` : base;
}

interface LegendItem {
  label: string;
  cls: string;
  amount: number;
  // % shown is "of committed" — only meaningful for in-fund buckets. Coinvest
  // and Excess live outside/over committed, so they omit the percentage.
  showPct: boolean;
}

function legendItems(fund: FundBreakdown): LegendItem[] {
  // Order matches the bar's physical stack (Invested -> Called -> Reserved
  // Expenses -> Reserved-for-Live -> Available) so legend reads left-to-right.
  const items: LegendItem[] = [
    { label: 'Invested', cls: styles.segInvested, amount: fund.invested, showPct: true },
    { label: 'Called Expenses', cls: styles.segCalled, amount: fund.called, showPct: true },
    { label: 'Reserved Expenses', cls: styles.segReservedExp, amount: fund.reservedExp, showPct: true },
    { label: 'Reserved for Live Deals', cls: styles.segReservedLive, amount: fund.reservedLive, showPct: true },
    { label: 'Available Capital', cls: styles.segAvailable, amount: fund.available, showPct: true },
  ];
  if (fund.coinvestTotal > 0) items.push({ label: 'Coinvest', cls: styles.coinvestFill, amount: fund.coinvestTotal, showPct: false });
  if (fund.excess > 0) items.push({ label: 'Excess (over committed)', cls: styles.excessSwatch, amount: fund.excess, showPct: false });
  return items;
}

function FundOverviewCard({ fund, trackMax, mode, onModeChange, uncalibrated, stageProbabilities }: {
  fund: FundBreakdown;
  trackMax: number; // shared $mm scale across modes — passed in, not per-mode
  mode: ReservationMode;
  onModeChange: (m: ReservationMode) => void;
  uncalibrated: boolean;
  stageProbabilities: StageProbability[]; // read-only, for the weights popover
}) {
  // Read-only "stage weights" popover (info-only; weights are set in Admin).
  const [showWeights, setShowWeights] = useState(false);
  const weightsAnchor = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showWeights) return;
    const onDocClick = (e: MouseEvent) => {
      if (weightsAnchor.current && !weightsAnchor.current.contains(e.target as Node)) {
        setShowWeights(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showWeights]);

  // trackMax is the SHARED scale (max track width across Absolute/Weighted), so
  // $-per-pixel stays constant when flipping; the lighter mode just fills less.
  const w = (v: number): string => `${(v / trackMax) * 100}%`;
  // Show the committed marker whenever the track extends past committed (in
  // EITHER mode, via the shared scale) so it stays a fixed reference line when
  // flipping — not mode-specific, which would make it flicker on/off.
  const showMarker = fund.committed < trackMax;

  // In-fund stack, ordered for attribution: fixed buckets first, then the
  // per-deal Reserved-Live blocks LAST so the Excess overlay lands on the
  // specific deals that pushed the fund over committed.
  const fixedSegs = [
    { key: 'invested', cls: styles.segInvested, amount: fund.invested, label: 'Invested' },
    { key: 'called', cls: styles.segCalled, amount: fund.called, label: 'Called Expenses' },
    { key: 'reservedExp', cls: styles.segReservedExp, amount: fund.reservedExp, label: 'Reserved Expenses' },
  ];
  const coinvestBlocks = fund.dealBlocks.filter((d) => d.coinvest > 0);

  return (
    <div className={styles.card}>
      <div className={styles.headerBar}>
        <span className={styles.headerLabel}>Fund II Overview</span>
        <span className={styles.headerSub}>Committed Capital Deployment</span>
        <div className={styles.modeToggle}>
          <button
            type="button"
            className={mode === 'absolute' ? styles.modeBtnActive : styles.modeBtn}
            onClick={() => onModeChange('absolute')}
            title="Reserve 100% of each live deal's Est EI Inv"
          >
            Absolute
          </button>
          <button
            type="button"
            className={mode === 'weighted' ? styles.modeBtnActive : styles.modeBtn}
            onClick={() => onModeChange('weighted')}
            title="Probability-weighted: each live + origination deal reserves Est EI Inv × P(stage)"
          >
            Weighted
          </button>
          <div className={styles.weightsAnchor} ref={weightsAnchor}>
            <button
              type="button"
              className={styles.infoBtn}
              onClick={() => setShowWeights((s) => !s)}
              aria-label="View stage weights"
              title="View stage weights (set in Admin)"
            >
              ⓘ
            </button>
            {showWeights && (
              <div className={styles.weightsPopover}>
                <div className={styles.weightsTitle}>Stage weights · weighted mode</div>
                <table className={styles.weightsTable}>
                  <tbody>
                    {stageProbabilities.length === 0 ? (
                      <tr><td className={styles.weightSection}>No stages mapped.</td></tr>
                    ) : (
                      stageProbabilities.map((s) => (
                        <tr key={s.stage_value} className={s.probability_pct === 0 ? styles.weightZero : undefined}>
                          <td>{s.stage_value}</td>
                          <td className={styles.weightSection}>{s.section}</td>
                          <td className={styles.weightPct}>{s.probability_pct}%</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                <div className={styles.weightsFooter}>Set in Admin → Fund Settings</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.committedRow}>
          <span className={styles.committedLabel}>Committed Capital</span>
          <span className={styles.committedValue}>
            {fmt(fund.committed)}mm
            {fund.excess > 0 && <span className={styles.overTally}> · {fmt(fund.excess)}mm over</span>}
            {fund.coinvestTotal > 0 && <span className={styles.coinvestTally}> / {fmt(fund.coinvestTotal)}mm coinvest</span>}
          </span>
        </div>

        {uncalibrated && (
          <div className={styles.uncalibratedNote}>
            Weighted mode isn’t calibrated — set stage probabilities in Admin → Fund Settings.
          </div>
        )}

        <div className={styles.bar}>
          {fixedSegs.map((s) => (s.amount > 0 ? (
            <div key={s.key} className={s.cls} style={{ width: w(s.amount) }} title={`${s.label}: ${fmt(s.amount)}mm`} />
          ) : null))}
          {/* Per-deal Reserved-Live blocks (orange) */}
          {fund.dealBlocks.map((d) => (
            <div key={`fund-${d.key}`} className={styles.segReservedLive} style={{ width: w(d.fund) }} title={dealTitle(d)} />
          ))}
          {/* Green slack up to the committed marker (over-allocated => 0 width) */}
          {fund.available > 0 && (
            <div className={styles.segAvailable} style={{ width: w(fund.available) }} title={`Available Capital: ${fmt(fund.available)}mm`} />
          )}
          {/* Coinvest (striped yellow), outside the fund */}
          {coinvestBlocks.map((d) => (
            <div key={`co-${d.key}`} className={styles.coinvestFill} style={{ width: w(d.coinvest) }} title={dealTitle(d)} />
          ))}
          {/* Committed boundary marker */}
          {showMarker && (
            <div className={styles.committedMarker} style={{ left: w(fund.committed) }} title={`Committed capital: ${fmt(fund.committed)}mm`} />
          )}
          {/* Excess = red outline over the spilled blocks (pointer-events: none so
              the underlying per-deal tooltips stay reachable — see CSS). */}
          {fund.excess > 0 && (
            <div className={styles.excessOverlay} style={{ left: w(fund.committed), width: w(fund.excess) }} />
          )}
        </div>

        <div className={styles.legend}>
          {legendItems(fund).map((it) => (
            <div key={it.label} className={styles.legendItem}>
              <div className={`${styles.legendDot} ${it.cls}`} />
              <div className={styles.legendText}>
                <div className={styles.legendLabel}>{it.label}</div>
                <div className={styles.legendValue}>
                  {fmt(it.amount)}mm
                  {it.showPct && (
                    <span className={styles.legendPct}>{((it.amount / fund.committed) * 100).toFixed(1)}%</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function OverviewPage() {
  const { data: analytics } = useAnalytics();
  const { data: fundSettings } = useFundSettings();

  // Reservation mode is a per-view lens (default Absolute), persisted locally.
  const [mode, setMode] = useState<ReservationMode>(() => {
    try {
      return localStorage.getItem('fundReservationMode') === 'weighted' ? 'weighted' : 'absolute';
    } catch {
      return 'absolute';
    }
  });
  const onModeChange = (m: ReservationMode) => {
    setMode(m);
    try { localStorage.setItem('fundReservationMode', m); } catch { /* ignore */ }
  };

  // "Include AI-proposed deals" — a page-wide LENS, persisted per browser exactly like the fund
  // bar's mode above. Candidate data rides on the same payload in its own fields, so flipping this
  // never refetches and never moves a committed number: it only decides what is drawn.
  //
  // ovw-2 (owner request, 2026-09-20): the lens now defaults **ON**. The stored value is still the
  // authority wherever there is one — the test is `!== 'false'`, so a browser that deliberately
  // turned the lens off keeps it off across deploys, and only a browser that never touched the
  // switch (or whose storage is unreadable) gets the new default. The key and its `'true'|'false'`
  // spelling are unchanged, so no browser is re-prompted or reset.
  const [includeCandidates, setIncludeCandidates] = useState<boolean>(() => {
    try {
      return localStorage.getItem('overviewIncludeCandidates') !== 'false';
    } catch {
      return true;
    }
  });
  const onIncludeCandidatesChange = (next: boolean) => {
    setIncludeCandidates(next);
    try { localStorage.setItem('overviewIncludeCandidates', next ? 'true' : 'false'); } catch { /* ignore */ }
  };

  // Compute BOTH modes regardless of the active toggle so the bar can use one
  // shared horizontal scale (max track width). Flipping the toggle then just
  // re-selects below — no recompute, and $-per-pixel stays constant.
  const breakdowns = useMemo(() => {
    if (!fundSettings || !analytics) return null;
    // `?? []` guards a stale/partial cached response missing the field.
    const stageProb = Object.fromEntries(
      (fundSettings.stage_probabilities ?? []).map((s) => [s.stage_value, s.probability_pct]),
    );
    // The backend sends live + origination together (`WEIGHTED_SECTIONS`); the split that used to
    // arrive as two props is now the section on each deal. Absolute mode reserves the live band;
    // weighted mode adds origination at P(stage).
    const fundDeals = analytics.fund_deals ?? [];
    const live = fundDeals.filter((d) => d.section === 'live');
    const abs = computeFundBreakdown(live, fundSettings, 'absolute', {});
    const wtd = computeFundBreakdown(fundDeals, fundSettings, 'weighted', stageProb);
    const trackMax = Math.max(trackWidth(abs), trackWidth(wtd)) || 1;
    return { abs, wtd, trackMax };
  }, [analytics, fundSettings]);

  const fund = breakdowns ? (mode === 'weighted' ? breakdowns.wtd : breakdowns.abs) : null;

  // The lens ships DARK: the switch appears only once the payload actually carries candidate data.
  // `proposed_summary` is the marker, and the test is `!== undefined`, not truthiness — the backend
  // sends an explicit `null` on the fail-closed (empty) payload, which IS a candidate-aware
  // response and must still offer the control. A response with the key absent is a pre-change
  // backend (or a React Query cache from one), and must not.
  const candidatesAvailable = analytics ? analytics.proposed_summary !== undefined : false;
  // A browser that flipped the lens on before a rollback would otherwise keep drawing empty
  // candidate series against a payload that no longer has them: the stored preference is kept, but
  // it only takes effect while the data is there.
  const lensOn = candidatesAvailable && includeCandidates;

  // Weighted mode with no calibrated stage → an empty bar that would otherwise
  // read as "nothing reserved" rather than "go set probabilities".
  const weightedUncalibrated = useMemo(() => {
    const probs = fundSettings?.stage_probabilities ?? [];
    return mode === 'weighted' && (probs.length === 0 || probs.every((s) => s.probability_pct === 0));
  }, [fundSettings, mode]);

  return (
    <div className={styles.root}>
      {fund && breakdowns && (
        <FundOverviewCard
          fund={fund}
          trackMax={breakdowns.trackMax}
          mode={mode}
          onModeChange={onModeChange}
          uncalibrated={weightedUncalibrated}
          stageProbabilities={fundSettings?.stage_probabilities ?? []}
        />
      )}

      {analytics && (
        <>
          <div className={styles.card}>
            <div className={styles.headerBar}>
              <span className={styles.headerLabel}>Pipeline Overview</span>
              {candidatesAvailable && (
                <label className={styles.candidateToggle}>
                  <input
                    type="checkbox"
                    role="switch"
                    className={styles.candidateSwitch}
                    checked={includeCandidates}
                    onChange={(e) => onIncludeCandidatesChange(e.target.checked)}
                  />
                  Include AI-proposed deals
                </label>
              )}
            </div>
            <div className={styles.body}>
              <h4 className={styles.subhead}>This Week</h4>
              <ThisWeekPanel metrics={analytics.summary_metrics} includeCandidates={lensOn} />
            </div>
          </div>

          <div className={styles.splitRow}>
            <PipelineByVertical
              data={analytics.by_vertical}
              dealTotal={analytics.vertical_deal_total}
              unclassified={analytics.vertical_unclassified ?? 0}
            />
            <PipelineBySector data={analytics.by_sector} includeCandidates={lensOn} />
          </div>

          <PipelineTrendChart
            data={analytics.trend_data}
            snapshotCount={analytics.snapshot_count}
            includeCandidates={lensOn}
          />

          {/* The by-team slot the native payload has always paid for and never rendered (A6) is
              where the per-person candidate view lives. Mounted only under the lens — with the
              toggle OFF the page keeps exactly the shape it has today. */}
          {lensOn && (
            <CandidateDealsByPerson
              data={analytics.proposed_by_person ?? []}
              summary={analytics.proposed_summary}
            />
          )}

          {/* The "Origination History" block that sat here — SectionDivider + ActivitySummaryCards
              + TeamActivityPanel — was removed with the Metrics tab (owner ruling 2026-09-01). It
              was Overview's only reader of `/api/analytics/team-activity`, whose builder reads the
              frozen `opportunities` snapshot; keeping it would have kept a dead builder alive to
              serve one card row. See this file's header. */}

          <EngagementHeatmap
            data={analytics.heatmap}
            weekLabels={analytics.heatmap_weeks}
            snapshotTimestamp={analytics.snapshot_timestamp}
          />
        </>
      )}
    </div>
  );
}
